import { HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { CasesRepository } from '../../../infrastructure/repositories/cases.repository';
import { CasesState } from '../../states/cases.state';
import { GetCasesListPaginatedQuery } from './get-cases-list-paginated.query';
import { GetCasesListPaginatedResult } from './get-cases-list-paginated.result';

/**
 * Get paginated cases list use case.
 * Calls server-side paginated API endpoint with skip/take parameters.
 *
 * Features:
 * - Server-side pagination (skip/take)
 * - Server-side search filtering (case number, case type, requestor name)
 * - State accumulation for infinite scroll (appends to existing data)
 *
 * NOTE: Uses infinite scroll pattern (append data) for dropdown UX,
 * not traditional pagination (replace data per page).
 */
@Injectable({ providedIn: 'root' })
export class GetCasesListPaginatedUseCase {
  private readonly _repository = inject(CasesRepository);
  private readonly _state = inject(CasesState);

  public execute(query: GetCasesListPaginatedQuery): Observable<GetCasesListPaginatedResult> {
    this._state.setLoadingCasesList(true);
    this._state.setErrorCasesList(null);

    // Build HTTP parameters per FE requirements
    let params = new HttpParams()
      .set('Skip', query.skip.toString())
      .set('Take', query.take.toString());

    // Add search filter if enabled and search term provided
    // Only build Filter parameter when:
    // 1. enableSearch is explicitly true AND
    // 2. search term has a value
    if (query.enableSearch === true && query.search && query.search.trim()) {
      const searchTerm = query.search.trim();
      // Build Kendo filter format for case number, type, and requestor name
      const filterJson = JSON.stringify({
        logic: 'or',
        filters: [
          { field: 'caseNumber', operator: 'contains', value: searchTerm },
          { field: 'caseTypeName', operator: 'contains', value: searchTerm },
          { field: 'requestorName', operator: 'contains', value: searchTerm },
        ],
      });
      params = params.set('Filter', filterJson);
    }

    // Call paginated API endpoint
    return this._repository.getCasesListPaginated(params).pipe(
      map((response) => ({
        data: response.data,
        totalElements: response.totalElements,
        totalPages: response.totalPages,
      })),
      tap((result) => {
        // Update state based on skip value
        if (query.skip === 0) {
          // First page or search reset - replace data
          this._state.setCasesList(result.data);
        } else {
          // Subsequent page - append data
          this._state.appendCasesList(result.data);
        }
        this._state.setCasesListTotalElements(result.totalElements);
        this._state.setLoadingCasesList(false);
      }),
      catchError((error) => {
        this._state.setErrorCasesList(error.message || 'Failed to get cases list');
        this._state.setLoadingCasesList(false);
        return throwError(() => error);
      })
    );
  }
}
